import type { ReactNode } from "react";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

export function AdminAccordionSection({
  title,
  badge,
  badgeUrgent,
  open,
  onToggle,
  plain,
  highlight,
  headerFireflyTargetId,
  children,
}: {
  title: string;
  badge?: number | null;
  badgeUrgent?: boolean;
  open: boolean;
  onToggle: () => void;
  plain?: boolean;
  highlight?: boolean;
  headerFireflyTargetId?: string;
  children: ReactNode;
}) {
  // `plain` drops outer card chrome (border/radius/fill) so nested admin
  // sections don't smash borders on mobile. Highlight keeps bordered pulse.
  // Dev-sync 714bacc0.
  const shellClass = plain
    ? highlight
      ? "overflow-hidden rounded-xl border border-amber-400 animate-admin-pending-pulse motion-reduce:animate-none dark:border-amber-500/90 dark:animate-admin-pending-pulse-dark"
      : "overflow-hidden"
    : highlight
      ? "overflow-hidden rounded-xl border border-amber-400 animate-admin-pending-pulse motion-reduce:animate-none dark:border-amber-500/90 dark:animate-admin-pending-pulse-dark"
      : "overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900";

  return (
    <div className={shellClass}>
      <button
        type="button"
        {...(headerFireflyTargetId ? { [FIREFLY_TARGET_ATTR]: headerFireflyTargetId } : {})}
        onClick={onToggle}
        className={
          "w-full flex items-center justify-between px-4 py-3 text-left transition-colors " +
          (highlight
            ? "hover:bg-amber-100/50 dark:hover:bg-amber-950/55"
            : plain
              ? "hover:bg-gray-50/80 dark:hover:bg-gray-800/50"
              : "hover:bg-gray-50 dark:hover:bg-gray-800/80")
        }
      >
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-900 dark:text-gray-100">{title}</span>
          {badge != null && badge > 0 && (
            <span
              className={
                "text-xs px-2 py-0.5 rounded-full font-medium " +
                (badgeUrgent ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-600")
              }
            >
              {badge}
            </span>
          )}
        </div>
        <span className="text-gray-400 dark:text-gray-500 text-xs">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div
          className={
            plain
              ? "border-t border-gray-100 dark:border-gray-700"
              : "border-t border-gray-100 dark:border-gray-700 px-4 pb-4 pt-3"
          }
        >
          {children}
        </div>
      )}
    </div>
  );
}
